import * as React from 'react';
import { FixMeLater } from '../../utils';
import { FieldBase, FieldBaseProps } from '../FieldBase';
import { InputBase, InputBaseProps } from '../InputBase';
import { StylerProps } from '../Styler';

export interface TextFieldProps
    extends Pick<
            FieldBaseProps,
            | 'fullWidth'
            | 'label'
            | 'shrinkLabel'
            | 'textAlign'
            | 'direction'
            | 'size'
            | 'variant'
            | 'showClear'
            | 'buttonIcon'
            | 'onButtonClick'
            | 'buttonRef'
            | 'width'
            | 'validationStatus'
            | 'icon'
            | 'startDecorator'
            | 'endDecorator'
            | 'chips'
        >,
        Omit<InputBaseProps, 'error'> {
    /**
     * A reference passed to the `input` element.
     */
    inputRef?: React.Ref<HTMLInputElement>;

    /**
     * Additional properties applied to the field's root element.
     */
    FieldRootProps?: StylerProps;
}

/**
 * TextField component allows a user to enter and edit text into a UI. They typically appear in forms and dialogs.
 * The TextField comes in various different sizes and states. It also provides multiple field variants to select from and use.
 * Additionally, we can specify labels and icons for the text field via props to provide more information to the user.
 */
export const TextField: React.ForwardRefExoticComponent<TextFieldProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function TextField(props: TextFieldProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            disabled,
            fullWidth,
            id,
            inputRef,
            label,
            textAlign,
            direction,
            style,
            shrinkLabel,
            size,
            variant,
            showClear,
            onButtonClick,
            buttonIcon,
            buttonRef,
            width,
            validationStatus,
            icon,
            startDecorator,
            endDecorator,
            chips,
            FieldRootProps,
            ...other
        } = props;

        return (
            <FieldBase
                ref={ref}
                id={id}
                disabled={disabled}
                label={label}
                fullWidth={fullWidth}
                textAlign={textAlign}
                direction={direction}
                shrinkLabel={shrinkLabel}
                size={size}
                variant={variant}
                showClear={showClear}
                buttonIcon={buttonIcon}
                onButtonClick={onButtonClick}
                buttonRef={buttonRef}
                width={width}
                validationStatus={validationStatus}
                icon={icon}
                startDecorator={startDecorator}
                endDecorator={endDecorator}
                chips={chips}
                {...FieldRootProps}
            >
                <InputBase
                    ref={inputRef}
                    isFieldBaseControl={true}
                    id={id as string}
                    disabled={disabled}
                    error={validationStatus === 'error'}
                    type="text"
                    {...other}
                />
            </FieldBase>
        );
    });
